/**
 * The policy manifest (docs/plans/2026-10-policy-manifest.md, roadmap D176): what one build of AI
 * Workbench (desktop and node alike) understands about its own endpoint configuration and
 * security policy, described well enough that an organisation's console can edit and validate
 * a policy for that build without knowing the build in advance.
 *
 *   settings          every declared setting (shared/settings/declarations.json): type, enum,
 *                     default, range, which layers may hold it, whether the organisation may lock
 *                     it, who enforces it, and a hint of what it touches (security, privacy, cost)
 *   policy.jsonSchema JSON Schema (draft 2020-12) of the whole `.mojoup/policy.json` the endpoint
 *                     accepts, generated from the zod schemas, `settings` / `settingsMode` filled
 *                     in key by key from the declarations
 *   policy.clauses    one row per clause: a title and description, the version it appeared in,
 *                     who enforces it, and `requiresPlatform` where the clause only means
 *                     something with a platform feature behind it (a taxonomy sync, an exporter,
 *                     the organisation's nodes)
 *   ui                the groups and their order, so a console renders editors generically
 *
 * The digest is SHA-256 over the canonical JSON of everything but `version`, `generatedAt`,
 * `digest` and `settingsDigest` ({@link policyManifestDigest}): two builds that understand the
 * same things carry the same digest, so a console fetches a manifest once per digest, not once
 * per release.
 *
 * Where it travels: the release asset `policy-manifest.json` (GitHub release and the customer
 * portal), and the machine itself — every fleet heartbeat carries `manifest: { version, digest }`
 * ({@link fleetManifestRefSchema}), and a heartbeat answer's `policyManifest` order
 * ({@link policyManifestOrderSchema}) has the machine `POST {@link POLICY_MANIFEST_ROUTE}` with
 * the body. A machine reports the policy keys it did not understand on the heartbeat
 * (`policy.unknownKeys`, {@link fleetPolicyReportSchema}).
 *
 * The AIOE mirrors this file (and ./policyManifestCheck.ts) byte for byte. Additive changes keep
 * `schemaVersion` 1; a reader ignores fields it does not know.
 */
// A namespace import: the renderer reaches this file through the shared schemas, and its dev server
// externalises node:crypto, which throws on a named import as the module loads. Only the digest needs it.
import * as nodeCrypto from "node:crypto";
import { z } from "zod";
export const POLICY_MANIFEST_SCHEMA_VERSION = 1;
export const POLICY_MANIFEST_PRODUCT = "ai-workbench";
/** The release asset's file name, beside `settings-declarations.json`. */
export const POLICY_MANIFEST_ASSET = "policy-manifest.json";
/** The JSON Schema of the manifest itself, published beside it. */
export const POLICY_MANIFEST_SCHEMA_ASSET = "policy-manifest.schema.json";
/** Where a machine sends its manifest when a heartbeat answer asks for it. */
export const POLICY_MANIFEST_ROUTE = "/fleet/v1/policy-manifest";
const hex64 = z.string().regex(/^[0-9a-f]{64}$/);
export const manifestSettingScopeSchema = z.enum(["global", "target", "project", "repo"]).describe("A settings layer: global (this machine), target (one WSL distro, container or SSH host), project, repo (committed in the repository).");
export const manifestSettingTypeSchema = z.enum(["string", "boolean", "number", "array", "object"]);
/** Who carries the value out: the workbench host on a desktop and a node alike (`both`), the desktop app itself (`workbench`), or only the headless node (`node`). */
export const manifestEnforcerSchema = z.enum(["workbench", "node", "both"]);
/** The editor a console shows for a value: derived from the type and enum. */
export const manifestWidgetSchema = z.enum(["switch", "select", "number", "text", "list", "json"]);
/** What a setting touches, so a console can group or flag it. Absent: an everyday preference. */
export const manifestSensitivitySchema = z.enum(["security", "privacy", "cost"]);
export const manifestSettingSchema = z.object({
    /** Without the `mjuWorkbench.` prefix, as a policy's `settings` names it. */
    key: z.string().min(1).max(200),
    type: manifestSettingTypeSchema,
    default: z.unknown().optional(),
    enum: z.array(z.string()).optional(),
    minimum: z.number().optional(),
    maximum: z.number().optional(),
    description: z.string().optional(),
    /** `ui.groups[].id`: the first segment of the key. */
    group: z.string().min(1).max(100),
    /** The layers a value may live in, lowest precedence first. */
    scopes: z.array(manifestSettingScopeSchema).min(1),
    /** A preference the desktop app keeps to itself (never a project's or repository's). */
    owner: z.literal("app").optional(),
    /** The value names something on one machine (a path, an endpoint, a port): it may differ per WSL distro or container. */
    machineSpecific: z.boolean(),
    /** The organisation may set it in a policy's `settings` and lock it there (`settingsMode: locked`): the endpoint honours it. */
    lockable: z.boolean(),
    enforcedBy: manifestEnforcerSchema,
    widget: manifestWidgetSchema,
    sensitivity: manifestSensitivitySchema.optional(),
});
export const manifestClauseSchema = z.object({
    /** Dotted path into the policy document: `dlp`, `dlp.labels`, `remote`. */
    path: z.string().min(1).max(200),
    title: z.string().min(1).max(200),
    /** What a person calls it in a notification or a sentence ("Backup models"), shorter than the title; absent from a manifest before D319. */
    label: z.string().min(1).max(200).optional(),
    description: z.string().min(1).max(4000),
    /** The first workbench version that carried the clause: a hint for machines too old to report a manifest. */
    since: z.string().regex(/^\d+\.\d+\.\d+/),
    enforcedBy: manifestEnforcerSchema,
    /**
     * True when the clause only does something with a platform feature behind it — the tenant's
     * label taxonomy synced, an exporter, the organisation's nodes or catalogue. A console can still
     * edit it from the manifest; it should say what the platform must provide (`platformFeature`).
     */
    requiresPlatform: z.boolean().optional(),
    /** What the platform provides for a `requiresPlatform` clause, in a sentence. */
    platformFeature: z.string().max(500).optional(),
    /** `ui.groups[].id` the clause is shown under. */
    group: z.string().min(1).max(100),
});
export const manifestGroupSchema = z.object({
    id: z.string().min(1).max(100),
    title: z.string().min(1).max(200),
    /** Ascending: the order a console lists the groups in. */
    order: z.number().int().nonnegative(),
    /** `settings`: a group of settings; `policy`: a group of clauses. */
    kind: z.enum(["settings", "policy"]),
});
/**
 * The words a console offers when it edits a team member (D251, docs/plans/2026-10-team-parity.md):
 * the workbench's role vocabulary, its agent runtimes, the thinking levels and the models it knows.
 * Optional: a manifest from before it has none, and the schema version does not move.
 */
export const manifestTeamsSchema = z.object({
    roles: z.array(z.object({ id: z.string().min(1).max(100), words: z.string().min(1).max(200) })).max(100),
    runtimes: z.array(z.object({ id: z.string().min(1).max(100), name: z.string().min(1).max(200) })).max(100),
    thinkingLevels: z.array(z.string().min(1).max(40)).max(20),
    models: z.array(z.object({ runtime: z.string().min(1).max(100), id: z.string().min(1).max(200), name: z.string().max(200).optional() })).max(2000),
});
export const policyManifestSchema = z.object({
    schemaVersion: z.literal(POLICY_MANIFEST_SCHEMA_VERSION),
    product: z.literal(POLICY_MANIFEST_PRODUCT),
    /** The build's version (`package.json`). */
    version: z.string().min(1).max(80),
    generatedAt: z.string(),
    /** {@link policyManifestDigest} of this document. */
    digest: hex64,
    /** SHA-256 of the declarations file this build carries: the `settingsDigest` the heartbeat already reports. */
    settingsDigest: hex64.optional(),
    settings: z.array(manifestSettingSchema),
    policy: z.object({
        /** JSON Schema (draft 2020-12) of `.mojoup/policy.json`. */
        jsonSchema: z.record(z.string(), z.unknown()),
        clauses: z.array(manifestClauseSchema),
    }),
    ui: z.object({ groups: z.array(manifestGroupSchema) }),
    /** What a console offers when it edits a team member (D251). */
    teams: manifestTeamsSchema.optional(),
    /**
     * What a person calls each field of `.mojoup/policy.json` (additive, schemaVersion stays 1): dotted path ->
     * `{ label }`, from the build's policy words (`POLICY_LABELS`, shared/wording/policyWords.ts), so a console
     * names `tools.denyCommands` the way the workbench does instead of by the bare path. A path not listed
     * reads through its longest labelled parent. Not part of the digest, as `teams` is not.
     */
    fields: z.record(z.string(), z.object({ label: z.string().min(1).max(200) })).optional(),
});
// ── Fleet ──────────────────────────────────────────────────────────────
/** `manifest` on a registration and a heartbeat: which manifest this machine runs. */
export const fleetManifestRefSchema = z.object({
    version: z.string().min(1).max(80),
    digest: hex64,
});
/** `policy` on a heartbeat: the organisation's policy as this machine understood it. */
export const fleetPolicyReportSchema = z.object({
    /** Dotted paths in the applied policy this build does not know (`settings.newKey`, `newClause`): kept in the file, ignored. */
    unknownKeys: z.array(z.string().min(1).max(300)).max(200),
});
/**
 * `policyManifest` on a heartbeat answer: send your manifest. The platform asks when it holds no
 * manifest for the digest the heartbeat reported, and repeats the order (same `id`) until it has
 * one; a machine acts on an id once, and again only after a while.
 */
export const policyManifestOrderSchema = z.object({
    id: z.string().min(1).max(100),
    /** The digest the platform is missing; informational — the machine sends the one it runs. */
    digest: hex64.optional(),
});
/** `POST /fleet/v1/policy-manifest`: 200/201/204 stored, 404 a platform without the route. */
export const policyManifestUploadSchema = z.object({
    schemaVersion: z.literal(POLICY_MANIFEST_SCHEMA_VERSION),
    deviceId: z.string().min(1).max(100),
    digest: hex64,
    version: z.string().min(1).max(80),
    manifest: policyManifestSchema,
});
// ── Digest ─────────────────────────────────────────────────────────────
/** JSON with object keys sorted at every level (arrays keep their order): the form the digest is taken over. */
export function canonicalJson(value) {
    if (value === null || typeof value !== "object")
        return JSON.stringify(value) ?? "null";
    if (Array.isArray(value))
        return `[${value.map((v) => (v === undefined ? "null" : canonicalJson(v))).join(",")}]`;
    const entries = Object.entries(value)
        .filter(([, v]) => v !== undefined)
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonicalJson(v)}`).join(",")}}`;
}
/** SHA-256 (hex) of what the manifest says, leaving out when and for which version it was written. */
export function policyManifestDigest(manifest) {
    const { schemaVersion, product, settings, policy, ui } = manifest;
    return nodeCrypto.createHash("sha256").update(canonicalJson({ schemaVersion, product, settings, policy, ui }), "utf8").digest("hex");
}
//# sourceMappingURL=policyManifest.js.map